/**
 * Zone-based reverb.
 *
 * Four procedurally generated impulse responses — open square, alley, small
 * interior, large interior — run in parallel off a single send. The listener's
 * surroundings are probed with a handful of raycasts a few times a second and the
 * result crossfades the four wet gains. Stepping out of an alley into a square
 * changes the tail of every gunshot in flight, which is the strongest "where am I"
 * cue the mix has.
 *
 * If the physics team is still a stub, the probe degrades to a fixed outdoor mix
 * rather than throwing.
 */
import * as THREE from 'three';
import { buildIRBuffer, ZONE_IR } from './synth/ir.js';
import { gain, bq } from './synth/nodes.js';

export const ZONE_ORDER = ['outdoor', 'alley', 'small_interior', 'large_interior'];

/** How loud each zone's send is, and how much pre-send low-cut it gets. */
const ZONE_MIX = {
  outdoor: { wet: 0.34, hpf: 140 },
  alley: { wet: 0.62, hpf: 110 },
  small_interior: { wet: 0.50, hpf: 120 },
  large_interior: { wet: 0.70, hpf: 90 },
};

const DIRS = [];
{
  const n = 10;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    DIRS.push(new THREE.Vector3(Math.cos(a), 0.06, Math.sin(a)).normalize());
  }
}
const UP = new THREE.Vector3(0, 1, 0);

export class Zones {
  /**
   * @param {BaseAudioContext} ac
   * @param {import('./Buses.js').Buses} buses
   */
  constructor(ac, buses, { seed = 0xa11e5 } = {}) {
    this.ac = ac;
    this.buses = buses;

    /** Voices send here. */
    this.send = gain(ac, 1);
    this.preHP = bq(ac, 'highpass', 110, 0.7);
    this.send.connect(this.preHP);

    this.nodes = {};
    ZONE_ORDER.forEach((name, i) => {
      const conv = ac.createConvolver();
      conv.normalize = true;
      conv.buffer = buildIRBuffer(ac, ZONE_IR[name], (seed + i * 7919) >>> 0);
      const g = gain(ac, 0);
      this.preHP.connect(conv).connect(g).connect(buses.input('verb'));
      this.nodes[name] = { conv, g, spec: ZONE_IR[name] };
    });

    this.weights = { outdoor: 1, alley: 0, small_interior: 0, large_interior: 0 };
    this.target = { ...this.weights };
    this.current = 'outdoor';
    this._probeTimer = 0;
    this._probeInterval = 0.22;
    this.openness = 1;
    this.ceiling = 99;
    this.meanDistance = 40;
    this._applyWeights(0);
  }

  /** Raw IR data for the self-test / debug overlay. */
  irBuffer(name) {
    return this.nodes[name]?.conv.buffer || null;
  }

  /**
   * Probe the space around `pos` using ctx.physics if it exists.
   * Sets meanDistance / ceiling / openness and picks target zone weights.
   */
  probe(ctx, pos) {
    const phys = ctx && ctx.physics;
    const cast = phys && typeof phys.raycast === 'function' ? phys.raycast.bind(phys) : null;
    if (!cast || !pos) { this._setTarget({ outdoor: 1 }); return; }

    const hitDist = (dir, far) => {
      try {
        const h = cast(pos, dir, far);
        if (!h) return far;
        const d = typeof h.distance === 'number' ? h.distance
          : (h.point ? pos.distanceTo(h.point) : far);
        return Number.isFinite(d) && d > 0 ? Math.min(d, far) : far;
      } catch {
        return far;
      }
    };

    const FAR = 60;
    let sum = 0;
    let escaped = 0;
    let minD = FAR;
    const dists = [];
    for (const d of DIRS) {
      const dist = hitDist(d, FAR);
      dists.push(dist);
      sum += dist;
      if (dist >= FAR - 0.5) escaped++;
      if (dist < minD) minD = dist;
    }
    const mean = sum / DIRS.length;
    const ceiling = hitDist(UP, 40);

    // "alleyness": two roughly opposite directions are close while the axis
    // perpendicular to them is open.
    let alleyness = 0;
    const n = DIRS.length;
    for (let i = 0; i < n / 2; i++) {
      const a = dists[i];
      const b = dists[i + n / 2];
      const perpA = dists[(i + Math.round(n / 4)) % n];
      const perpB = dists[(i + Math.round((3 * n) / 4)) % n];
      if (a < 6 && b < 6) {
        const open = Math.min(perpA, perpB);
        alleyness = Math.max(alleyness, Math.min(1, open / 14) * (1 - Math.max(a, b) / 6));
      }
    }

    this.meanDistance = mean;
    this.ceiling = ceiling;
    this.openness = escaped / DIRS.length;

    const indoors = ceiling < 9;
    const w = { outdoor: 0, alley: 0, small_interior: 0, large_interior: 0 };
    if (indoors) {
      const big = Math.min(1, Math.max(0, (mean - 7) / 12));
      w.large_interior = big;
      w.small_interior = 1 - big;
      // an indoor corridor still flutters
      w.alley = Math.min(0.35, alleyness * 0.5);
    } else {
      w.alley = alleyness;
      w.outdoor = 1 - alleyness * 0.75;
      if (mean < 9) {
        const enclosed = Math.min(1, (9 - mean) / 7);
        w.large_interior = enclosed * 0.35 * (1 - alleyness);
      }
    }
    this._setTarget(w);
  }

  _setTarget(w) {
    let total = 0;
    for (const k of ZONE_ORDER) total += Math.max(0, w[k] || 0);
    if (total <= 1e-6) { this.target = { outdoor: 1, alley: 0, small_interior: 0, large_interior: 0 }; return; }
    const t = {};
    for (const k of ZONE_ORDER) t[k] = Math.max(0, w[k] || 0) / total;
    this.target = t;
    let best = ZONE_ORDER[0];
    for (const k of ZONE_ORDER) if (t[k] > t[best]) best = k;
    this.current = best;
  }

  /** Crossfade toward the target weights. */
  update(dt, ctx, listenerPos) {
    this._probeTimer -= dt;
    if (this._probeTimer <= 0) {
      this._probeTimer = this._probeInterval;
      this.probe(ctx, listenerPos);
    }
    const k = 1 - Math.exp(-dt * 2.2);
    let changed = false;
    for (const name of ZONE_ORDER) {
      const d = (this.target[name] - this.weights[name]) * k;
      if (Math.abs(d) > 1e-5) { this.weights[name] += d; changed = true; }
    }
    if (changed) this._applyWeights(0.08);

    // low-cut on the send follows the dominant zone
    const hp = ZONE_MIX[this.current]?.hpf ?? 110;
    this.preHP.frequency.setTargetAtTime(hp, this.ac.currentTime, 0.15);
  }

  _applyWeights(smooth) {
    const t = this.ac.currentTime;
    for (const name of ZONE_ORDER) {
      const v = this.weights[name] * (ZONE_MIX[name]?.wet ?? 0.5);
      const p = this.nodes[name].g.gain;
      if (smooth > 0) p.setTargetAtTime(v, t, smooth);
      else p.setValueAtTime(v, t);
    }
  }

  /** Force a zone (used by the debug shot and by the level team if it wants to). */
  setZone(name, immediate = false) {
    if (!ZONE_ORDER.includes(name)) return;
    const w = { outdoor: 0, alley: 0, small_interior: 0, large_interior: 0 };
    w[name] = 1;
    this.target = w;
    this.current = name;
    if (immediate) { this.weights = { ...w }; this._applyWeights(0); }
  }

  /** Indoor-ness 0..1, used by the ambience mix. */
  get indoor() {
    return this.weights.small_interior + this.weights.large_interior + this.weights.alley * 0.35;
  }

  describe() {
    return {
      current: this.current,
      weights: { ...this.weights },
      openness: this.openness,
      ceiling: this.ceiling,
      meanDistance: this.meanDistance,
    };
  }
}
